import {
  asValueFrom,
  combineLatestTyped,
  mergeTyped,
  useDataStream,
  useStreamEffect,
  useStreamValue,
  useValueAsStream,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { map, mapTo, switchMap } from 'rxjs/operators';
import { updateProjectImagePartially } from '../../../api/fetch-functions/project-image';
import { ProjectImageInfoAPIRequestBodyType } from '../../../api/types/project-image-info/api-request-body';
import { floorOptions } from '../../../constants/floor-options';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { useFetchResultState } from '../../../functions/hooks/use-fetch-result-state';
import {
  FormInputStateConfigType,
  useFormInputState
} from '../../../functions/hooks/use-form-input-state';
import {
  FormSelectStateConfigType,
  useFormSelectState
} from '../../../functions/hooks/use-form-select-state';
import { token$ } from '../../../global-state/auth-state';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { mapListLatest } from '../../../global-state/database/common/map-latest';
import { modelListOf } from '../../../global-state/database/model-list';
import { fetchProjectImageAnnotationListOf } from '../../../global-state/database/project-image-annotation-list';
import { fetchProjectImageListOf } from '../../../global-state/database/project-image-list';
import {
  companyId$,
  imageId$,
  koumutenId$,
  projectId$
} from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { IProjectImageInfoType } from '../../../types/record/image-info';
import { IModelType } from '../../../types/record/model';
import { ProjectImageSettingsDialogRedirectView } from './project-image-settings-dialog-redirect';
import { ProjectImageSettingsDialogView } from './project-image-settings-dialog-view';

const modelIdSelectorStateConfig: FormSelectStateConfigType<ProjectImageInfoAPIRequestBodyType> = {
  required: true,
  requiredCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.inferenceModel,
  validationErrorContentMapFn: content => content.modelId?.toArray() ?? []
};

const imageNameInputStateConfig: FormInputStateConfigType<ProjectImageInfoAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.imageName,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.imageName,
  required: true,
  validationErrorContentMapFn: content => content.name?.toArray() ?? []
};

interface Props {
  mode: 'init' | 'edit';
  open: boolean;
  onClose: () => void;
  projectImage: IProjectImageInfoType;
}

export const ProjectImageSettingsDialog: FC<Props> = memo(
  ({
    mode,
    open,
    onClose,
    projectImage: projectImageInit
  }: Readonly<Props>) => {
    const projectImageInit$ = useValueAsStream(projectImageInit);

    const modelList$ = useDataStream<ListWithPromiseState<IModelType>>(
      listWithPromiseStateInit<IModelType>(),
      companyId$.pipe(switchMap(modelListOf))
    );

    const availableModels$ = useDataStream<ListWithPromiseState<IModelType>>(
      listWithPromiseStateInit<IModelType>(),
      modelList$.pipe(
        map(list => mapListLatest(list, l => l.filter(e => e.canBeUsed)))
      )
    );

    // form

    const [
      // dummy comment
      fetchResult,
      setFetchResult
    ] = useFetchResultState<
      IProjectImageInfoType,
      ProjectImageInfoAPIRequestBodyType
    >();

    const nonFieldErrors = useMemo<string[]>(
      () =>
        fetchResult !== undefined &&
        fetchResult.type === 'error' &&
        fetchResult.body.type === 'non-field-errors'
          ? fetchResult.body.content.toArray()
          : [],
      [fetchResult]
    );

    const [
      modelId$,
      modelIdErrors$,
      modelIdValueIsValid$,
      setModelId
    ] = useFormSelectState<
      IProjectImageInfoType,
      ProjectImageInfoAPIRequestBodyType,
      number
    >(open, fetchResult, modelIdSelectorStateConfig, projectImageInit.modelId);

    const [floorId, setFloorId] = useState<string | undefined>(undefined);

    const [
      imageName$,
      imageNameErrors$,
      imageNameIsValid$,
      imageNameOnInput
    ] = useFormInputState<
      IProjectImageInfoType,
      ProjectImageInfoAPIRequestBodyType
    >(open, fetchResult, imageNameInputStateConfig, projectImageInit.name);

    const [submitOnClick$, submitOnClick] = useVoidEventAsStream();

    const valuesAreValid$ = useDataStream<boolean>(
      false,
      combineLatestTyped(imageNameIsValid$, modelIdValueIsValid$).pipe(
        map(
          ([imageNameIsValid, modelIdValueIsValid]) =>
            imageNameIsValid && modelIdValueIsValid
        )
      )
    );

    const submitButtonEnabled$ = useDataStream<boolean>(
      false,
      mergeTyped(submitOnClick$.pipe(mapTo(false)), valuesAreValid$)
    );

    useStreamEffect(
      submitOnClick$.pipe(
        asValueFrom(
          combineLatestTyped(
            token$,
            projectId$,
            valuesAreValid$,
            projectImageInit$,
            imageName$,
            modelId$,
            imageId$
          )
        )
      ),
      ([
        token,
        projectId,
        valuesAreValid,
        projectImageInit,
        imageName,
        modelId,
        imageId
      ]) => {
        if (token !== undefined && valuesAreValid) {
          updateProjectImagePartially(token)(
            projectId,
            projectImageInit.id
          )({
            name: imageName,
            modelId
          }).then(res => {
            setFetchResult(res);
            if (res.type === 'success') {
              onClose();
              fetchProjectImageListOf(projectId);
              fetchProjectImageAnnotationListOf(projectId, imageId);
            }
          });
        }
      }
    );

    // extracted values

    const submitButtonEnabled = useStreamValue(submitButtonEnabled$, false);
    const imageName = useStreamValue(imageName$);
    const imageNameErrors = useStreamValue(imageNameErrors$);
    const koumutenId = useStreamValue(koumutenId$);
    const companyId = useStreamValue(companyId$);
    const modelId = useStreamValue(modelId$);
    const modelIdErrors = useStreamValue(modelIdErrors$);
    const availableModels = useStreamValue(availableModels$);

    // side effects
    const autoCompleteButtonIsDisabled = modelId === undefined;

    const onAutoCompleteClick = useCallback(() => {
      const modelListLatest = availableModels?.value.latest;
      if (autoCompleteButtonIsDisabled || modelListLatest === undefined) return;

      const floorName =
        floorOptions.find(e => e.id === floorId?.toString())?.label ?? '';
      const modelName = modelListLatest.find(e => e.id === modelId)?.name ?? '';
      imageNameOnInput(`${floorName}${modelName}`);
    }, [
      autoCompleteButtonIsDisabled,
      imageNameOnInput,
      availableModels,
      modelId,
      floorId
    ]);

    useLoadingPage(
      koumutenId === undefined ||
        companyId === undefined ||
        availableModels === undefined ||
        availableModels.status === 'pending'
    );

    return (
      <>
        {koumutenId === undefined ||
        companyId === undefined ||
        availableModels === undefined ? (
          undefined
        ) : availableModels.value.latest.isEmpty() ? (
          <ProjectImageSettingsDialogRedirectView
            open={open}
            onClose={onClose}
            koumutenId={koumutenId}
            companyId={companyId}
          />
        ) : (
          <ProjectImageSettingsDialogView
            mode={mode}
            open={open}
            onCancelClick={onClose}
            onOkClick={submitOnClick}
            imgSrc={projectImageInit.fileUri}
            availableModels={availableModels}
            modelId={modelId}
            setModelId={setModelId}
            modelIdErrors={modelIdErrors}
            floorId={floorId}
            setFloorId={setFloorId}
            imageName={imageName}
            imageNameOnInput={imageNameOnInput}
            imageNameErrors={imageNameErrors}
            autoCompleteButtonIsDisabled={autoCompleteButtonIsDisabled}
            onAutoCompleteClick={onAutoCompleteClick}
            submitButtonEnabled={submitButtonEnabled}
            nonFieldErrors={nonFieldErrors}
          />
        )}
      </>
    );
  }
);

ProjectImageSettingsDialog.displayName = 'ProjectImageSettingsDialog';
